/**
 * Shopify line-item properties for the Google / Instagram standee setup.
 *
 * Shopify uses the visible field label as the property key, so the key
 * depends on the storefront language (DE `/`, EN `/en`, FR `/fr`) and on the
 * theme version the customer shopped with. Billbee forwards these properties
 * unchanged as `OrderItem.Attributes`. Every known variant is listed here
 * explicitly — legacy Billbee orders must keep resolving forever.
 *
 * When the theme renames a field again, add the new key here first.
 */

export const StandeeSetupMode = {
  PRECONFIGURED: 'PRECONFIGURED',
  SELF_SETUP: 'SELF_SETUP',
} as const;
export type StandeeSetupMode =
  (typeof StandeeSetupMode)[keyof typeof StandeeSetupMode];

export type StandeeTargetKind = 'google' | 'instagram';

export type StandeePropertyField =
  | 'googleBusinessQuery'
  | 'googlePlaceId'
  | 'instagramUsername'
  | 'setupMode';

/**
 * - `legacy`: pre-#8559 keys from older theme generations / other languages
 * - `old`: theme up to #UP2-PROMO-ORD-8558
 * - `current`: theme from #8559 (2026-10-13 18:31 UTC)
 * - `upcoming`: next theme push (adds the setup choice)
 */
export type StandeePropertyEra = 'legacy' | 'old' | 'current' | 'upcoming';

export type StandeePropertyAlias = {
  key: string;
  field: StandeePropertyField;
  era: StandeePropertyEra;
};

export const STANDEE_PROPERTY_ALIASES: readonly StandeePropertyAlias[] = [
  // googleBusinessQuery
  {
    key: 'Name & Adresse auf Google',
    field: 'googleBusinessQuery',
    era: 'upcoming',
  }, // DE + FR
  {
    key: 'Name & address on Google',
    field: 'googleBusinessQuery',
    era: 'upcoming',
  }, // EN
  {
    key: 'Name & Adresse bei Google (optional)',
    field: 'googleBusinessQuery',
    era: 'current',
  }, // DE + FR
  {
    key: 'Name & address on Google (optional)',
    field: 'googleBusinessQuery',
    era: 'current',
  }, // EN
  {
    key: 'Name & Adresse ihres Geschäfts',
    field: 'googleBusinessQuery',
    era: 'old',
  }, // DE
  {
    key: 'Name & address of your business',
    field: 'googleBusinessQuery',
    era: 'old',
  }, // EN
  {
    key: 'Nom et adresse de votre magasin',
    field: 'googleBusinessQuery',
    era: 'old',
  }, // FR
  {
    key: 'Namen & Adresse Ihres Geschäfts',
    field: 'googleBusinessQuery',
    era: 'legacy',
  },
  {
    key: 'Nombre y dirección de su empresa',
    field: 'googleBusinessQuery',
    era: 'legacy',
  },
  {
    key: "Nome e indirizzo dell'azienda",
    field: 'googleBusinessQuery',
    era: 'legacy',
  },
  {
    key: 'Naam en adres van uw bedrijf',
    field: 'googleBusinessQuery',
    era: 'legacy',
  },
  { key: 'Nazwa i adres firmy', field: 'googleBusinessQuery', era: 'legacy' },
  {
    key: 'Nom & adresse de votre magasin',
    field: 'googleBusinessQuery',
    era: 'legacy',
  },
  {
    key: 'Nom et adresse de votre entreprise',
    field: 'googleBusinessQuery',
    era: 'legacy',
  },
  {
    key: 'Nombre y dirección de su negocio',
    field: 'googleBusinessQuery',
    era: 'legacy',
  },

  // googlePlaceId — same key in every language since #8559
  { key: 'Google Place ID', field: 'googlePlaceId', era: 'current' },

  // instagramUsername
  {
    key: 'Instagram-Benutzername',
    field: 'instagramUsername',
    era: 'upcoming',
  }, // DE + FR
  { key: 'Instagram username', field: 'instagramUsername', era: 'upcoming' }, // EN
  {
    key: 'Instagram-Benutzername (optional)',
    field: 'instagramUsername',
    era: 'current',
  }, // DE + FR
  {
    key: 'Instagram username (optional)',
    field: 'instagramUsername',
    era: 'current',
  }, // EN
  { key: 'Benutzername auf Instagram', field: 'instagramUsername', era: 'old' }, // DE
  {
    key: 'Nutzername Ihres Instagram-Account',
    field: 'instagramUsername',
    era: 'legacy',
  },
  {
    key: 'Nutzername von Ihrem Instagram-Account',
    field: 'instagramUsername',
    era: 'legacy',
  },
  { key: 'Username on Instagram', field: 'instagramUsername', era: 'legacy' },
  {
    key: 'Nombre de usuario en Instagram',
    field: 'instagramUsername',
    era: 'legacy',
  },
  {
    key: "Nom d'utilisateur sur Instagram",
    field: 'instagramUsername',
    era: 'legacy',
  },
  {
    key: 'Nome utente su Instagram',
    field: 'instagramUsername',
    era: 'legacy',
  },
  {
    key: 'Gebruikersnaam op Instagram',
    field: 'instagramUsername',
    era: 'legacy',
  },
  {
    key: 'Nazwa użytkownika na Instagramie',
    field: 'instagramUsername',
    era: 'legacy',
  },

  // setupMode — FR falls back to the EN theme strings
  { key: 'Einrichtung', field: 'setupMode', era: 'upcoming' }, // DE
  { key: 'Setup', field: 'setupMode', era: 'upcoming' }, // EN + FR
];

const SETUP_MODE_VALUES: Readonly<Record<string, StandeeSetupMode>> = {
  'Vorkonfiguriert erhalten': StandeeSetupMode.PRECONFIGURED,
  'Get pre-configured': StandeeSetupMode.PRECONFIGURED,
  'Später einrichten': StandeeSetupMode.SELF_SETUP,
  'Set up later': StandeeSetupMode.SELF_SETUP,
};

/**
 * Keys that may appear on standee line items but carry no setup data
 * (variant option names — always German). Not reported as unknown.
 */
const IGNORED_KEYS: ReadonlySet<string> = new Set([
  'Farbe',
  'Sprache',
  'Größe',
  'Color',
]);

/** Variant option values (always German). Billbee adds the variant title, e.g. `Schwarz / Deutsch / S`, as an attribute key. */
const VARIANT_OPTION_VALUES: ReadonlySet<string> = new Set([
  'Schwarz',
  'Weiß',
  'Blau',
  'Deutsch',
  'Englisch',
  'Französisch',
  'Spanisch',
  'Niederländisch',
  'S',
  'L',
]);

function isVariantTitleKey(key: string): boolean {
  const segments = key.split(' / ').map((segment) => segment.trim());
  return (
    segments.length > 1 &&
    segments.every((segment) => VARIANT_OPTION_VALUES.has(segment))
  );
}

const ERA_PRIORITY: Readonly<Record<StandeePropertyEra, number>> = {
  upcoming: 0,
  current: 1,
  old: 2,
  legacy: 3,
};

const ALIAS_BY_KEY: ReadonlyMap<string, StandeePropertyAlias> = new Map(
  STANDEE_PROPERTY_ALIASES.map((alias) => [alias.key, alias]),
);

const PLACE_ID_PATTERN = /^[A-Za-z0-9_-]{10,}$/;

export type StandeeLineItemProperty = {
  name: string;
  value?: string | null;
};

export type StandeePropertyMatch = StandeePropertyAlias & { value: string };

export type NormalizedStandeeProperties = {
  googleBusinessQuery?: string;
  googlePlaceId?: string;
  instagramUsername?: string;
  setupMode: StandeeSetupMode;
  /**
   * `property`: explicit customer choice. `default`: no setup property on the
   * line item (every order before the setup choice shipped) → PRECONFIGURED.
   */
  setupModeSource: 'property' | 'default';
  /** PRECONFIGURED but no target data for the standee kind — manual follow-up. */
  needsManualFollowUp: boolean;
  matches: StandeePropertyMatch[];
  /** Non-hidden keys not in the alias map. Log these so renames surface. */
  unknownKeys: string[];
  /** Unknown keys containing "instagram" used as a fallback username source. */
  heuristicKeys: string[];
  warnings: string[];
};

/** Trim, strip a leading `@` and `instagram.com/` URL prefixes. Free text with spaces is only trimmed. */
export function normalizeInstagramUsername(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed || /\s/.test(trimmed)) return trimmed;
  return trimmed
    .replace(/^@+/, '')
    .replace(/^(?:https?:\/\/)?(?:www\.)?instagram\.com\//i, '')
    .replace(/[?#].*$/, '')
    .replace(/\/+$/, '')
    .replace(/^@+/, '');
}

export function parseStandeeSetupMode(
  value: string,
): StandeeSetupMode | undefined {
  return SETUP_MODE_VALUES[value.trim()];
}

export function findStandeePropertyAlias(
  key: string,
): StandeePropertyAlias | undefined {
  return ALIAS_BY_KEY.get(key.trim());
}

function pickByEra(
  matches: StandeePropertyMatch[],
  field: StandeePropertyField,
): StandeePropertyMatch | undefined {
  let best: StandeePropertyMatch | undefined;
  for (const match of matches) {
    if (match.field !== field) continue;
    if (!best || ERA_PRIORITY[match.era] < ERA_PRIORITY[best.era]) {
      best = match;
    }
  }
  return best;
}

/**
 * Normalize one line item's properties into canonical fields. Process per
 * line item (multi-location carts carry one target per line item).
 */
export function normalizeStandeeLineItemProperties(
  properties: readonly StandeeLineItemProperty[],
  kind: StandeeTargetKind,
): NormalizedStandeeProperties {
  const matches: StandeePropertyMatch[] = [];
  const unknownKeys: string[] = [];
  const heuristicCandidates: { key: string; value: string }[] = [];
  const warnings: string[] = [];

  for (const property of properties) {
    const key = property.name.trim();
    const value = (property.value ?? '').trim();
    if (
      !key ||
      key.startsWith('_') ||
      IGNORED_KEYS.has(key) ||
      isVariantTitleKey(key)
    ) {
      continue;
    }

    const alias = ALIAS_BY_KEY.get(key);
    if (!alias) {
      unknownKeys.push(key);
      if (value && /instagram/i.test(key)) {
        heuristicCandidates.push({ key, value });
      }
      continue;
    }
    if (value) matches.push({ ...alias, value });
  }

  const result: NormalizedStandeeProperties = {
    setupMode: StandeeSetupMode.PRECONFIGURED,
    setupModeSource: 'default',
    needsManualFollowUp: false,
    matches,
    unknownKeys,
    heuristicKeys: [],
    warnings,
  };

  const query = pickByEra(matches, 'googleBusinessQuery');
  if (query) result.googleBusinessQuery = query.value;

  const placeId = pickByEra(matches, 'googlePlaceId');
  if (placeId) {
    if (PLACE_ID_PATTERN.test(placeId.value)) {
      result.googlePlaceId = placeId.value;
    } else {
      warnings.push(`Ignoring malformed Google Place ID "${placeId.value}"`);
    }
  }

  const instagram = pickByEra(matches, 'instagramUsername');
  if (instagram) {
    result.instagramUsername = normalizeInstagramUsername(instagram.value);
  } else if (kind === 'instagram' && heuristicCandidates.length > 0) {
    const candidate = heuristicCandidates[0]!;
    result.instagramUsername = normalizeInstagramUsername(candidate.value);
    result.heuristicKeys = heuristicCandidates.map((c) => c.key);
  }

  const setup = pickByEra(matches, 'setupMode');
  if (setup) {
    const mode = parseStandeeSetupMode(setup.value);
    if (mode) {
      result.setupMode = mode;
      result.setupModeSource = 'property';
    } else {
      warnings.push(
        `Unrecognized setup value "${setup.value}" for "${setup.key}" — treating as pre-configured`,
      );
    }
  }

  const hasTarget =
    kind === 'google'
      ? Boolean(result.googlePlaceId || result.googleBusinessQuery)
      : Boolean(result.instagramUsername);
  result.needsManualFollowUp =
    result.setupMode === StandeeSetupMode.PRECONFIGURED && !hasTarget;

  return result;
}

export function buildGoogleWriteReviewUrl(placeId: string): string {
  return `https://search.google.com/local/writereview?placeid=${encodeURIComponent(placeId)}`;
}

export type GoogleStandeeTarget =
  /** Use the Place ID directly. Never run a name search on this path. */
  | { strategy: 'place_id'; placeId: string; reviewUrl: string }
  /** No Place ID: text search fallback, the match must be confirmed by a human. */
  | { strategy: 'text_search'; query: string }
  /** Customer connects the target in the app. */
  | { strategy: 'self_setup' }
  /** Pre-configured but no target data on the line item. */
  | { strategy: 'manual_follow_up' };

export function resolveGoogleStandeeTarget(
  normalized: Pick<
    NormalizedStandeeProperties,
    'googlePlaceId' | 'googleBusinessQuery' | 'setupMode'
  >,
): GoogleStandeeTarget {
  if (normalized.setupMode === StandeeSetupMode.SELF_SETUP) {
    return { strategy: 'self_setup' };
  }
  if (normalized.googlePlaceId) {
    return {
      strategy: 'place_id',
      placeId: normalized.googlePlaceId,
      reviewUrl: buildGoogleWriteReviewUrl(normalized.googlePlaceId),
    };
  }
  if (normalized.googleBusinessQuery) {
    return { strategy: 'text_search', query: normalized.googleBusinessQuery };
  }
  return { strategy: 'manual_follow_up' };
}
